import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getRun, exportRunUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Download, ArrowLeft, FileJson, FileSpreadsheet, FileText, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { formatRelative } from "@/lib/format";

const STATUS_PT = { success: "sucesso", partial: "parcial", error: "erro", running: "a correr" };

export default function RunResultsPage() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    getRun(runId).then(setRun).catch(e => setErr(e?.response?.data?.detail || e.message)).finally(() => setLoading(false));
  }, [runId]);

  // Background runs: keep refreshing while status === "running"
  useEffect(() => {
    if (run?.status !== "running") return undefined;
    const t = setTimeout(() => { getRun(runId).then(setRun).catch(() => {}); }, 3000);
    return () => clearTimeout(t);
  }, [run, runId]);

  if (loading) return <div className="p-8 text-sm text-muted-foreground">A carregar captura…</div>;
  if (err) return <div className="p-8 text-sm text-destructive">{err}</div>;
  if (!run) return null;

  if (run.status === "running") {
    const p = run.progress || {};
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1400px] mx-auto space-y-4" data-testid="run-running">
        <Button asChild variant="ghost" size="sm"><Link to="/"><ArrowLeft className="h-4 w-4 mr-1"/> Voltar às receitas</Link></Button>
        <Card className="p-6 flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary"/>
          <div>
            <div className="font-medium">A recolher dados em segundo plano…</div>
            <div className="text-sm text-muted-foreground" data-testid="run-running-progress">
              {p.total ? `${p.done} de ${p.total} concluído(s)` : "a iniciar"} · esta página atualiza sozinha
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1400px] mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm" data-testid="back-to-recipes-button">
          <Link to="/"><ArrowLeft className="h-4 w-4 mr-1"/> Voltar às receitas</Link>
        </Button>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{fontFamily:'Space Grotesk'}}>Resultado da captura</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Concluída {formatRelative(run.finished_at)} · <span className="mono">{run.url}</span></p>
        </div>
        <Badge variant="outline" data-testid="run-status-badge" className={run.status === 'success' ? 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30' : 'bg-destructive/15 text-destructive border-destructive/30'}>
          {run.status === 'success' ? <CheckCircle2 className="h-3.5 w-3.5 mr-1"/> : <AlertTriangle className="h-3.5 w-3.5 mr-1"/>}
          {STATUS_PT[run.status] || run.status}
        </Badge>
      </div>

      {run.status !== 'success' && run.status !== 'partial' && (
        <Alert variant="destructive" data-testid="error-banner">
          <AlertTriangle className="h-4 w-4"/>
          <AlertTitle>A captura falhou</AlertTitle>
          <AlertDescription className="mono text-xs">{run.error || 'Erro desconhecido'}</AlertDescription>
        </Alert>
      )}

      {run.per_day && run.per_day.length > 0 && (
        <Card className="p-4" data-testid="run-per-day-card">
          <div className="text-sm font-semibold mb-2" style={{fontFamily:'Space Grotesk'}}>
            Recolha por dia ({run.range_start || run.dates?.[0] || "—"} → {run.range_end || run.dates?.[run.dates.length - 1] || "—"})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {run.per_day.map((d, i) => (
              <Badge key={i} variant="outline" className={d.status === 'success' ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30' : 'bg-destructive/10 text-destructive border-destructive/30'}>
                {d.date} · {d.rows} linhas
              </Badge>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="text-sm text-muted-foreground">{(run.rows || []).length} rows · {(run.columns || []).length} columns</div>
          <div className="flex items-center gap-2">
            <Button asChild size="sm" variant="outline" data-testid="results-export-csv-button">
              <a href={exportRunUrl(run.id, 'csv')}><FileText className="h-3.5 w-3.5 mr-1"/> CSV</a>
            </Button>
            <Button asChild size="sm" variant="outline" data-testid="results-export-json-button">
              <a href={exportRunUrl(run.id, 'json')}><FileJson className="h-3.5 w-3.5 mr-1"/> JSON</a>
            </Button>
            <Button asChild size="sm" data-testid="results-export-xlsx-button">
              <a href={exportRunUrl(run.id, 'xlsx')}><FileSpreadsheet className="h-3.5 w-3.5 mr-1"/> Excel</a>
            </Button>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-auto border border-border rounded-lg scrollbar-thin" data-testid="results-table-wrap">
          <table className="w-full text-xs" data-testid="results-table">
            <thead className="bg-muted sticky top-0">
              <tr>{(run.columns || []).map((h,i)=>(<th key={i} className="text-left px-3 py-2 border-b border-border whitespace-nowrap">{h}</th>))}</tr>
            </thead>
            <tbody>
              {(run.rows || []).map((r,ri)=>(
                <tr key={ri} className="hover:bg-muted/40">
                  {r.map((c,ci)=>(<td key={ci} className="px-3 py-2 border-b border-border align-top whitespace-pre-wrap mono">{c}</td>))}
                </tr>
              ))}
              {(run.rows || []).length === 0 && (
                <tr><td colSpan={(run.columns || []).length || 1} className="text-center text-muted-foreground py-10">Nenhum dado extraído.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
